/**
 * White-label theme provider.
 *
 * Must be the outermost wrapper in the React component tree for all
 * white-labeled portals. On mount, fetches branding configuration from
 * the White-Label Service keyed to `window.location.hostname` and
 * injects CSS custom properties onto `:root`. Results are cached in
 * localStorage for 10 minutes to avoid redundant fetches across navigations.
 *
 * CSS custom properties injected:
 * - `--ipa-primary`     — primary brand colour
 * - `--ipa-secondary`   — secondary brand colour
 * - `--ipa-accent`      — accent brand colour
 * - `--ipa-font-family` — brand font-family stack
 *
 * Logo URL is intentionally NOT injected as a CSS custom property. Injecting a
 * URL from an external API into CSS (e.g. `background-image: url(...)`) would
 * allow a compromised White-Label Service to load attacker-controlled resources.
 * Instead, {@link ThemeAwareLogo} reads `branding.logoUrl` directly from context
 * and validates the `https:` scheme before rendering an `<img>` element.
 *
 * @module shared/theme/ThemeProvider
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ThemeContext } from './ThemeContext';
import type { BrandingConfig } from './whitelabel-api';
import { DEFAULT_BRANDING_CONFIG, resolveBranding } from './whitelabel-api';

/** Cache TTL in milliseconds (10 minutes). */
const CACHE_TTL_MS = 10 * 60 * 1000;

/** localStorage key prefix for cached branding configs. */
const CACHE_KEY_PREFIX = 'ipa_theme_';

interface CacheEntry {
  branding: BrandingConfig;
  fetchedAt: number;
}

/** Reads a cached branding entry from localStorage, or null if missing/expired. */
function readCache(domain: string): BrandingConfig | null {
  try {
    const raw = localStorage.getItem(`${CACHE_KEY_PREFIX}${domain}`);
    if (!raw) return null;
    const entry = JSON.parse(raw) as CacheEntry;
    if (Date.now() - entry.fetchedAt > CACHE_TTL_MS) {
      localStorage.removeItem(`${CACHE_KEY_PREFIX}${domain}`);
      return null;
    }
    return entry.branding;
  } catch {
    return null;
  }
}

/** Writes a branding entry to localStorage with a timestamp. */
function writeCache(domain: string, branding: BrandingConfig): void {
  try {
    const entry: CacheEntry = { branding, fetchedAt: Date.now() };
    localStorage.setItem(`${CACHE_KEY_PREFIX}${domain}`, JSON.stringify(entry));
  } catch {
    // Storage unavailable — silently ignore.
  }
}

/** Injects branding CSS custom properties onto the document root element. */
function applyThemeToRoot(branding: BrandingConfig): void {
  const root = document.documentElement;
  root.style.setProperty('--ipa-primary', branding.primaryColor);
  root.style.setProperty('--ipa-secondary', branding.secondaryColor);
  root.style.setProperty('--ipa-accent', branding.accentColor);
  root.style.setProperty('--ipa-font-family', branding.fontFamily);
  // Note: logo is rendered via ThemeAwareLogo using branding.logoUrl directly.
  // Avoid injecting a logo URL into CSS to prevent CSS injection from a
  // compromised or misconfigured White-Label Service response.
}

/** Props for the ThemeProvider component. */
export interface ThemeProviderProps {
  /**
   * Child components. ThemeProvider must be the outermost wrapper so
   * all descendants can access the resolved branding via {@link useTheme}.
   */
  children: React.ReactNode;
  /**
   * Override the domain used for branding resolution. Defaults to
   * `window.location.hostname`. Pass a value in tests to avoid
   * reading from the browser environment.
   */
  domain?: string;
}

/**
 * Outermost theme provider for white-labeled portals.
 *
 * Resolves branding for the current domain, injects CSS custom properties
 * onto `:root`, and exposes the resolved config via the theme context.
 * Renders a brief loading indicator while the first fetch is in progress
 * to prevent a flash of unstyled content.
 */
export function ThemeProvider({ children, domain }: ThemeProviderProps): React.JSX.Element {
  const resolvedDomain = domain ?? (typeof window !== 'undefined' ? window.location.hostname : '');

  // Read cache once atomically to avoid multiple localStorage reads and potential
  // inconsistency between the branding and loading state initialisers.
  const initialCached = readCache(resolvedDomain);
  if (initialCached) {
    applyThemeToRoot(initialCached);
  }

  const [branding, setBranding] = useState<BrandingConfig>(initialCached ?? DEFAULT_BRANDING_CONFIG);
  const [loading, setLoading] = useState<boolean>(initialCached === null);
  const [error, setError] = useState<string | null>(null);

  const fetchBranding = useCallback(async (): Promise<void> => {
    // Skip if a fresh cache entry already populated state during init.
    const cached = readCache(resolvedDomain);
    if (cached) {
      setBranding(cached);
      applyThemeToRoot(cached);
      setLoading(false);
      return;
    }

    try {
      const resolved = await resolveBranding(resolvedDomain);
      writeCache(resolvedDomain, resolved);
      setBranding(resolved);
      applyThemeToRoot(resolved);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to load theme';
      setError(message);
      // Fall back to IPA defaults so the page remains usable.
      applyThemeToRoot(DEFAULT_BRANDING_CONFIG);
    } finally {
      setLoading(false);
    }
  }, [resolvedDomain]);

  useEffect(() => {
    void fetchBranding();
  }, [fetchBranding]);

  const contextValue = useMemo(
    () => ({ branding, loading, error }),
    [branding, loading, error],
  );

  return (
    <ThemeContext.Provider value={contextValue}>
      {loading ? (
        <div
          role="status"
          aria-label="Loading theme"
          data-testid="theme-loading"
          style={{
            position: 'fixed',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: '#ffffff',
            zIndex: 9999,
          }}
        >
          <div
            style={{
              width: '40px',
              height: '40px',
              border: '3px solid #e2e8f0',
              borderTopColor: '#1a73e8',
              borderRadius: '50%',
              animation: 'ipa-theme-spin 0.8s linear infinite',
            }}
          />
          <style>{`@keyframes ipa-theme-spin { to { transform: rotate(360deg); } }`}</style>
        </div>
      ) : (
        children
      )}
    </ThemeContext.Provider>
  );
}
